import React, { useState } from 'react';
import { Edit2, X, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

const ProfileChangeRequestForm = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || ''
  });
  const [requestReason, setRequestReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Track what changed
  const getChanges = () => {
    const changes = {};
    if (formData.name !== user.name) changes.name = formData.name;
    if (formData.email !== user.email) changes.email = formData.email;
    if (formData.phone !== user.phone) changes.phone = formData.phone;
    return changes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const changes = getChanges();
    
    if (Object.keys(changes).length === 0) {
      setError('No changes detected');
      setLoading(false);
      return;
    }

    try {
      await api.post('/users/change-requests', {  // ✅ FIXED: Changed from /change-request to /change-requests
        requestedChanges: changes,
        requestReason: requestReason || 'Profile update request'
      });

      setSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit change request');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = Object.keys(getChanges()).length > 0;

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Request Submitted!
            </h3>
            <p className="text-gray-600">
              Your profile change request has been submitted to the admin for approval.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-orange-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Edit2 className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Request Profile Changes</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-orange-600 rounded p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Current vs Requested */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Changes require admin approval. Your current information will remain until the request is approved.
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Current:</p>
                <p className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-2 rounded">
                  {user.name}
                </p>
              </div>
              <div className="text-orange-500">→</div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Requested:</p>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Current:</p>
                <p className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-2 rounded">
                  {user.email}
                </p>
              </div>
              <div className="text-orange-500">→</div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Requested:</p>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Current:</p>
                <p className="text-sm font-medium text-gray-700 bg-gray-50 px-3 py-2 rounded">
                  {user.phone || 'Not set'}
                </p>
              </div>
              <div className="text-orange-500">→</div>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Requested:</p>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Change (Optional)
            </label>
            <textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Explain why you need these changes..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !hasChanges}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <span>Submit Request</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileChangeRequestForm;